import * as TtfeTracker from '@src/analytics/TtfeTracker';
import {
  createAudioSidecar,
  initialSidecarPlanState,
  nextSidecarAction,
} from '@src/services/upload/sidecar';
import { hasAudioSidecarDevOverride } from '@src/utility/audioSidecarFlag';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useRef, useState } from 'react';

import type { StoryCreationService } from '@src/services/StoryCreationService';
import type {
  SidecarPlanState,
  SidecarResult,
  SidecarSkipReason,
} from '@src/services/upload/sidecar';

const logger = LogManager.getLogger('upload:useAudioSidecar');

type Params = {
  modalId: string;
  storyCreationService: Pick<StoryCreationService, 'uploadAudioSidecar'>;
  /** The blank story's id once created (`blankStoryRef.current?.id`). */
  getStoryId: () => string | undefined;
  getOrganizationId: () => string | undefined;
};

export type AudioSidecarControls = {
  /** Start the demux for a newly picked single local file. */
  start: (file: File) => void;
  /** Cancel the in-flight demux (e.g. the file was rejected). */
  abort: () => void;
  /** Feed the Original's upload progress; `done` means it landed. */
  onVideoProgress: (progress: number, done?: boolean) => void;
  /** The Original finished uploading. */
  onVideoLanded: () => void;
  /** Validation passed and the blank story exists: the Sidecar may upload. */
  markUploadReady: (clerkToken: string | undefined) => void;
  /** Emit telemetry held back until the TTFE session started. */
  flushTelemetry: () => void;
  /** The Original upload is being retried from 0 %. */
  resetVideoProgress: () => void;
  /** State, so the processing-start effect re-runs when the Sidecar lands. */
  landed: boolean;
  /** S3 key of the landed Sidecar, or null. */
  getKey: () => string | null;
  /** Drop the landed Sidecar (its processing start failed). */
  forgetKey: () => void;
};

/**
 * Audio Sidecar orchestration (PLAT-11697): demuxed at file-select, uploaded
 * ahead of the video. Decisions are in `nextSidecarAction` (pure, unit-tested);
 * this hook only records transitions and performs the chosen action. Nothing
 * here is ever surfaced to the user: every failure returns to the
 * Server-Extract Path. Choosing which upload starts processing stays with the
 * caller (`decideProcessingStart`).
 *
 * The plan lives in refs because every transition happens in upload
 * callbacks, outside render; `landed` is state so the caller's processing-start
 * effect re-runs.
 */
export function useAudioSidecar(params: Params): AudioSidecarControls {
  const { modalId, storyCreationService, getStoryId, getOrganizationId } =
    params;
  const planRef = useRef<SidecarPlanState>(initialSidecarPlanState());
  const resultRef = useRef<SidecarResult | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const keyRef = useRef<string | null>(null);
  const clerkTokenRef = useRef<string | undefined>(undefined);
  const [landed, setLanded] = useState<boolean>(false);

  // Closing the Add Story flow must not leave the worker reading the file.
  useEffect(() => () => abortRef.current?.abort(), []);

  const sessionKey = () => getStoryId() ?? modalId;

  // The demux starts before `TtfeTracker.fileSelected` (it runs ahead of
  // validation), so its events may be ready before the session exists. Hold
  // them and flush once the clock has started; TtfeTracker dedupes.
  const telemetryRef = useRef<Array<() => void>>([]);
  const flushTelemetry = () => {
    if (!TtfeTracker.hasSession(sessionKey())) return;
    const pending = telemetryRef.current;
    telemetryRef.current = [];
    pending.forEach(emit => emit());
  };
  const track = (emit: () => void) => {
    telemetryRef.current.push(emit);
    flushTelemetry();
  };

  const giveUp = (reason: SidecarSkipReason) => {
    const plan = planRef.current;
    if (plan.givenUp) return;
    plan.givenUp = true;
    abortRef.current?.abort();
    resultRef.current = null;
    logger.info('sidecar-given-up', {
      modalId,
      storyId: getStoryId(),
      reason,
      videoPercent: plan.videoPercent,
    });
    track(() => TtfeTracker.sidecarSkipped(sessionKey(), reason));
  };

  const runUpload = async () => {
    const result = resultRef.current;
    const storyId = getStoryId();
    if (!result || !storyId) {
      giveUp('unknown-error');
      return;
    }
    const uploaded = await storyCreationService.uploadAudioSidecar(
      result.blob,
      storyId,
      getOrganizationId(),
      clerkTokenRef.current,
    );
    const plan = planRef.current;
    if (!uploaded.ok) {
      giveUp('sidecar-upload-failed');
      return;
    }
    // The Original may have landed mid-flight; the pipeline then started from
    // it and this object is simply unused.
    if (plan.givenUp) return;
    plan.sidecarLanded = true;
    keyRef.current = uploaded.key;
    logger.info('sidecar-landed', {
      modalId,
      storyId,
      key: uploaded.key,
      wallMs: uploaded.wallMs,
      videoPercent: plan.videoPercent,
    });
    track(() =>
      TtfeTracker.sidecarUploadCompleted(sessionKey(), {
        wallMs: uploaded.wallMs,
        bytes: result.blob.size,
      }),
    );
    setLanded(true);
  };

  const reconcile = () => {
    const plan = planRef.current;
    const action = nextSidecarAction(plan);
    if (action.type === 'give-up') {
      giveUp(action.reason);
    } else if (action.type === 'upload') {
      plan.sidecarUploadStarted = true;
      void runUpload();
    }
  };

  const start = (file: File) => {
    abortRef.current?.abort();
    const plan = initialSidecarPlanState();
    plan.enabled = true;
    planRef.current = plan;
    resultRef.current = null;
    keyRef.current = null;
    const controller = new AbortController();
    abortRef.current = controller;
    logger.info('sidecar-demux-start', {
      modalId,
      fileSizeBytes: file.size,
    });
    void createAudioSidecar(file, {
      signal: controller.signal,
      // Decision D vs L: the dev override lets the > 4 GiB demo fixture take
      // the Sidecar Path for the measurement run (see demuxAudioSidecar.ts).
      allowCo64Over4GiB: hasAudioSidecarDevOverride(),
    }).then(outcome => {
      if (planRef.current !== plan) return; // a newer file superseded this one
      if (outcome.result) {
        plan.demux = 'ready';
        resultRef.current = outcome.result;
        logger.info('sidecar-demux-complete', {
          modalId,
          demuxMs: outcome.result.demuxMs,
          sidecarBytes: outcome.result.sidecarBytes,
          codec: outcome.result.codec,
        });
        const { result } = outcome;
        track(() =>
          TtfeTracker.sidecarDemuxCompleted(sessionKey(), {
            demuxMs: result.demuxMs,
            originalBytes: result.originalBytes,
            sidecarBytes: result.sidecarBytes,
            gatePass: result.gate.pass,
          }),
        );
      } else {
        plan.demux = 'skipped';
        logger.info('sidecar-demux-skipped', {
          modalId,
          reason: outcome.skipReason,
          detail: outcome.detail,
        });
        if (outcome.skipReason !== 'aborted') {
          const { gate, demuxMs, skipReason } = outcome;
          if (gate) {
            track(() =>
              TtfeTracker.sidecarDemuxCompleted(sessionKey(), {
                demuxMs: demuxMs ?? 0,
                originalBytes: file.size,
                sidecarBytes: 0,
                gatePass: gate.pass,
              }),
            );
          }
          track(() => TtfeTracker.sidecarSkipped(sessionKey(), skipReason));
        }
      }
      reconcile();
    });
  };

  const onVideoProgress = (progress: number, done?: boolean) => {
    const plan = planRef.current;
    if (!plan.enabled) return;
    plan.videoPercent = Math.max(plan.videoPercent, progress);
    if (done) plan.videoLanded = true;
    reconcile();
  };

  const onVideoLanded = () => {
    if (!planRef.current.enabled) return;
    planRef.current.videoLanded = true;
    reconcile();
  };

  const markUploadReady = (clerkToken: string | undefined) => {
    clerkTokenRef.current = clerkToken;
    if (!planRef.current.enabled) return;
    planRef.current.uploadReady = true;
    reconcile();
  };

  return {
    start,
    abort: () => abortRef.current?.abort(),
    onVideoProgress,
    onVideoLanded,
    markUploadReady,
    flushTelemetry,
    resetVideoProgress: () => {
      planRef.current.videoPercent = 0;
    },
    landed,
    getKey: () => keyRef.current,
    forgetKey: () => {
      keyRef.current = null;
    },
  };
}
